import React from 'react'
import { FieldErrors, SubmitHandler } from 'react-hook-form'
import get from 'lodash/get'
import { ControlledHiddenInput } from 'transfix-form/components'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import { Button } from 'transfix-ui/components/Button'
import { IEdiMessage } from 'types/graphqlTypes'
import { useTransactionSets } from '../../../../EdiSelfServiceOutbound/hooks/useTransactionSets'
import { useEdiMessageTypeMutations } from '../../hooks/useEdiMessageTypeMutations'
import { TConnectionConfigType, TEdiMessageTypeSchema } from './EdiMessageTypeSchema'
import { useEdiMessageTypeForm } from './useEdiMessageTypeForm'
import { useFormOptionsBuilders } from './useFormOptionsBuilders'

const getErrorMessage = <T extends FieldErrors<TEdiMessageTypeSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

export interface IEdiMessageTypeFormProps {
  /**
   * if we are creating a new message type, which inbound/outbound connection config should it be associated with?
   * this is a 2-part identifier, paired with connectionConfigType
   *
   * @example 10
   */
  connectionConfigId: string

  /**
   * which kind of config are we managing message types for?
   * this is a 2-part identifier, paired with connectionConfigId
   *
   * @example 'InboundConnectionConfig'
   */
  connectionConfigType: TConnectionConfigType

  /**
   * if this is provided, we do an update. otherwise we do a create.
   */
  ediMessageType?: IEdiMessage | null

  /**
   * what to do when user clicks 'cancel' in the form
   */
  onCancel: () => void

  /**
   * what to do when user submits the form
   */
  onSubmitSuccess: SubmitHandler<TEdiMessageTypeSchema>
}

export const EdiMessageTypeForm = ({
  connectionConfigId,
  connectionConfigType,
  ediMessageType,
  onCancel,
  onSubmitSuccess,
}: IEdiMessageTypeFormProps) => {
  const { buildFormValues } = useEdiMessageTypeMutations()
  const { control, errors, handleSubmit } = useEdiMessageTypeForm({
    defaultValues: buildFormValues({
      ediMessageType,
      connectionConfigType,
      connectionConfigId,
    }),
  })
  const {
    configurableBuilderOptions,
    configurableParserOptions,
    legacyBuilderOptions,
    legacyParserOptions,
  } = useFormOptionsBuilders()

  const onSubmitError = () => {}
  const submitHandler = handleSubmit(onSubmitSuccess, onSubmitError)

  const { selectOptions: transactionSetOptions } = useTransactionSets()

  // TODO: how to show spinners while submitHandler is running, and then stop once it finishes?
  return (
    <form onSubmit={submitHandler} data-test='EdiMessageTypeForm'>
      <ControlledHiddenInput control={control} name='id' />
      <ControlledHiddenInput control={control} name='connectionConfigType' />
      <ControlledHiddenInput control={control} name='connectionConfigId' />

      <ControlledSelect
        control={control}
        name='transactionSetWithSubtype'
        SelectProps={{
          label: 'Transaction Set',
          options: transactionSetOptions,
          dataTest: 'transaction-set-with-subtype',
          error: !!getErrorMessage(errors, 'transactionSetWithSubtype'),
          required: true,
        }}
      />

      {connectionConfigType === 'InboundConnectionConfig' && (
        <>
          <ControlledSelect
            control={control}
            name='ediMapperConfigurableParserId'
            SelectProps={{
              label: 'Self-serve parser',
              options: configurableParserOptions,
              placeholder: 'Choose a self-server parser',
              dataTest: 'edi-mapper-configurable-parser-id',
              error: !!getErrorMessage(errors, 'ediMapperConfigurableParserId'),
            }}
          />
          <ControlledSelect
            control={control}
            name='legacyParserClass'
            SelectProps={{
              label: 'Classic parser',
              options: legacyParserOptions,
              placeholder: 'Choose a classic parser',
              dataTest: 'legacy-parser-class',
              error: !!getErrorMessage(errors, 'legacyParserClass'),
            }}
          />
        </>
      )}

      {connectionConfigType === 'OutboundConnectionConfig' && (
        <>
          <ControlledSelect
            control={control}
            name='ediMapperConfigurableBuilderId'
            SelectProps={{
              label: 'Self-serve builder',
              options: configurableBuilderOptions,
              placeholder: 'Choose a self-server builder',
              dataTest: 'edi-mapper-configurable-builder-id',
              error: !!getErrorMessage(errors, 'ediMapperConfigurableBuilderId'),
            }}
          />

          <ControlledSelect
            control={control}
            defaultValue={ediMessageType?.legacyBuilderClass || ''}
            name='legacyBuilderClass'
            SelectProps={{
              label: 'Classic builder',
              options: legacyBuilderOptions,
              placeholder: 'Choose a classic builder',
              dataTest: 'legacy-builder-class',
              error: !!getErrorMessage(errors, 'legacyBuilderClass'),
            }}
          />
        </>
      )}

      <Button type='submit' color='primary' data-test='submit-button'>
        {ediMessageType?.id ? 'Update' : 'Save'}
      </Button>
      <Button onClick={onCancel} data-test='cancel-button'>
        Cancel
      </Button>
    </form>
  )
}
